import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FirmanteEnvioSimple } from '../../models/firmante-envio-simple';
import {
  esTelefonoValido,
  MENSAJE_TELEFONO_FORMATO_INVALIDO,
  MENSAJE_TELEFONO_REQUERIDO,
  telefonoEstaVacio
} from '../../utils/telefono-validacion.util';

@Component({
  selector: 'app-envio-simple-firmante-card',
  templateUrl: './envio-simple-firmante-card.component.html',
  styleUrls: ['./envio-simple-firmante-card.component.css']
})
export class EnvioSimpleFirmanteCardComponent implements OnChanges {
  private readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  readonly mensajeTelefonoRequerido = MENSAJE_TELEFONO_REQUERIDO;
  readonly mensajeTelefonoFormatoInvalido = MENSAJE_TELEFONO_FORMATO_INVALIDO;
  readonly mensajeEmailDuplicado = 'E-mail duplicado.';
  readonly mensajeTelefonoDuplicado = 'Teléfono duplicado.';

  @Input() firmante!: FirmanteEnvioSimple;
  @Input() indice = 0;
  @Input() mostrarEmail = true;
  @Input() mostrarTelefono = false;
  @Input() puedeEliminar = false;
  @Input() forzarValidacionContacto = false;
  @Input() emailDuplicado = false;
  @Input() telefonoDuplicado = false;
  /** Paridad dosier: en envío paralelo se oculta el número de orden (se muestra "-"). */
  @Input() ocultarOrden = false;

  @Output() datosChange = new EventEmitter<Partial<Pick<FirmanteEnvioSimple, 'nombre' | 'apellido1' | 'apellido2' | 'email' | 'telefono'>>>();
  @Output() buscarEmpleado = new EventEmitter<void>();
  @Output() editarOtrosDatos = new EventEmitter<void>();
  @Output() eliminar = new EventEmitter<void>();

  emailTocado = false;
  telefonoTocado = false;
  private emailDuplicadoDescartado = false;
  private telefonoDuplicadoDescartado = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['emailDuplicado']?.currentValue === true) {
      this.emailDuplicadoDescartado = false;
    }
    if (changes['telefonoDuplicado']?.currentValue === true) {
      this.telefonoDuplicadoDescartado = false;
    }
  }

  get ordenDisplay(): string {
    return this.ocultarOrden ? '-' : `${this.indice + 1}º`;
  }

  get emailMuestraErrorDuplicado(): boolean {
    return this.mostrarEmail && this.emailDuplicado && !this.emailDuplicadoDescartado;
  }

  get emailTieneErrorFormato(): boolean {
    if (!this.mostrarEmail || (!this.emailTocado && !this.forzarValidacionContacto)) {
      return false;
    }
    const email = this.firmante.email?.trim() ?? '';
    return email.length === 0 || !this.emailRegex.test(email);
  }

  get emailTieneError(): boolean {
    return this.emailTieneErrorFormato || this.emailMuestraErrorDuplicado;
  }

  get emailEsRequeridoConError(): boolean {
    if (!this.emailTieneErrorFormato) {
      return false;
    }
    const email = this.firmante.email?.trim() ?? '';
    return email.length === 0;
  }

  get mensajeErrorEmail(): string {
    if (this.emailEsRequeridoConError) {
      return 'Requerido.';
    }
    if (this.emailTieneErrorFormato) {
      return 'Debe ser un email correcto.';
    }
    return this.mensajeEmailDuplicado;
  }

  marcarEmailTocado(): void {
    this.emailTocado = true;
  }

  onEmailInput(valor: string): void {
    this.emailTocado = true;
    this.emailDuplicadoDescartado = true;
    this.actualizarCampo('email', valor);
  }

  get telefonoMuestraErrorDuplicado(): boolean {
    return this.mostrarTelefono && this.telefonoDuplicado && !this.telefonoDuplicadoDescartado;
  }

  get telefonoTieneErrorFormato(): boolean {
    if (!this.mostrarTelefono || (!this.telefonoTocado && !this.forzarValidacionContacto)) {
      return false;
    }
    const telefono = this.firmante.telefono?.trim() ?? '';
    return telefono.length === 0 || !esTelefonoValido(telefono);
  }

  get telefonoTieneError(): boolean {
    return this.telefonoTieneErrorFormato || this.telefonoMuestraErrorDuplicado;
  }

  get telefonoEsRequeridoConError(): boolean {
    if (!this.telefonoTieneErrorFormato) {
      return false;
    }
    return telefonoEstaVacio(this.firmante.telefono);
  }

  get mensajeErrorTelefono(): string {
    if (this.telefonoEsRequeridoConError) {
      return this.mensajeTelefonoRequerido;
    }
    if (this.telefonoTieneErrorFormato) {
      return this.mensajeTelefonoFormatoInvalido;
    }
    return this.mensajeTelefonoDuplicado;
  }

  marcarTelefonoTocado(): void {
    this.telefonoTocado = true;
  }

  onTelefonoInput(valor: string): void {
    this.telefonoTocado = true;
    this.telefonoDuplicadoDescartado = true;
    this.actualizarCampo('telefono', valor);
  }

  actualizarCampo(
    campo: 'nombre' | 'apellido1' | 'apellido2' | 'email' | 'telefono',
    valor: string
  ): void {
    this.datosChange.emit({ [campo]: valor });
  }
}
